import { motion } from "framer-motion";
import { useState } from "react";
import { Users, FileCheck, Plane, Battery, TrendingDown, ShieldCheck, LucideIcon } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";
import { Button } from "@/components/ui/button";

interface Spec {
  icon: LucideIcon;
  label: string;
  value: string;
  description: string;
}

export function ModelsShowcase() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<"7seat" | "5seat">("7seat");

  const specsPC: Spec[] = [
    {
      icon: Users,
      label: t("hero.cargoVolume"),
      value: t("modelsShowcase.specs.seatsAndTrunk"),
      description: t("modelsShowcase.specs.seatsAndTrunkDesc")
    },
    {
      icon: Battery,
      label: t("hero.range"),
      value: "350km",
      description: t("modelsShowcase.specs.rangeDesc")
    },
    {
      icon: TrendingDown,
      label: t("modelsShowcase.specs.economical"),
      value: t("modelsShowcase.specs.economicalValue"),
      description: t("modelsShowcase.specs.economicalDesc")
    },
    {
      icon: FileCheck,
      label: t("modelsShowcase.specs.license"),
      value: t("modelsShowcase.specs.licenseValue"),
      description: t("modelsShowcase.specs.licenseDesc")
    },
    {
      icon: Plane,
      label: t("modelsShowcase.specs.rideshare"),
      value: t("modelsShowcase.specs.rideshareValue"),
      description: t("modelsShowcase.specs.rideshareDesc")
    },
    {
      icon: ShieldCheck,
      label: t("modelsShowcase.specs.topSpeed"),
      value: t("modelsShowcase.specs.topSpeedValue"),
      description: t("modelsShowcase.specs.topSpeedDesc")
    }
  ];

  const specsLGV: Spec[] = [
    {
      icon: Users,
      label: t("hero.cargoVolume"),
      value: t("modelsShowcase.specs.seatsAndTrunkLGV"),
      description: t("modelsShowcase.specs.seatsAndTrunkDescLGV")
    },
    {
      icon: Battery,
      label: t("hero.range"),
      value: "350km",
      description: t("modelsShowcase.specs.rangeDesc")
    },
    {
      icon: TrendingDown,
      label: t("modelsShowcase.specs.economical"),
      value: t("modelsShowcase.specs.economicalValue"),
      description: t("modelsShowcase.specs.economicalDesc")
    },
    {
      icon: FileCheck,
      label: t("modelsShowcase.specs.license"),
      value: t("modelsShowcase.specs.licenseValueLGV"),
      description: t("modelsShowcase.specs.licenseDescLGV")
    },
    {
      icon: Plane,
      label: t("modelsShowcase.specs.cargo"),
      value: t("modelsShowcase.specs.cargoValue"),
      description: t("modelsShowcase.specs.cargoDesc")
    },
    {
      icon: ShieldCheck,
      label: t("modelsShowcase.specs.topSpeed"),
      value: t("modelsShowcase.specs.topSpeedValue"),
      description: t("modelsShowcase.specs.topSpeedDesc")
    }
  ];

  const highlights = [
    t("modelsShowcase.highlights.space"),
    t("modelsShowcase.highlights.driveByWire"),
    t("modelsShowcase.highlights.zeroEmission"),
    t("modelsShowcase.highlights.economical")
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="container mx-auto px-4">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4 px-6 py-2 bg-blue-100 rounded-full">
            <span className="text-blue-600 font-semibold">{t("modelsShowcase.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("modelsShowcase.title")}
            <span className="block text-blue-600">{t("modelsShowcase.titleHighlight")}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t("modelsShowcase.subtitle")}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-2xl p-2 shadow-lg border border-gray-200">
            <button
              onClick={() => setActiveTab("7seat")}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === "7seat"
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              PC
            </button>
            <button
              onClick={() => setActiveTab("5seat")}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === "5seat"
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              LGV
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === "7seat" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-7xl mx-auto"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-white">
                  <img src="/img/V7-背景透明1.webp"
                    alt="長安跨越 V7-EV PC"
                    className="w-full h-full object-contain"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
                {/* Price Badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-2xl shadow-2xl">
                  <p className="text-sm font-medium opacity-90">{t("modelsShowcase.price")}</p>
                  <p className="text-3xl font-bold">Please contact us via WhatsApp</p>
                </div>
              </div>

              {/* Details */}
              <div>
                <h3 className="text-3xl md:text-4xl font-bold mb-2">長安跨越 V7-EV PC</h3>
                <p className="text-blue-600 font-semibold text-lg mb-8">{t("modelsShowcase.available")}</p>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {specsPC.map((spec, index) => {
                    const Icon = spec.icon;
                    return (
                      <motion.div
                        key={spec.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                        className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-500 mb-1">{spec.label}</p>
                            <p className="text-xl font-bold text-gray-900 truncate">{spec.value}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{spec.description}</p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Highlights */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-8">
                  <h4 className="text-white font-bold text-lg mb-4">{t("modelsShowcase.coreHighlights")}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {highlights.map((highlight) => (
                      <div key={highlight} className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="flex gap-4">
                  <Button className="flex-1 h-14 text-lg">
                    {t("common.bookTestDrive")}
                  </Button>
                  <Button variant="outline" className="flex-1 h-14 text-lg">
                    {t("common.learnMore")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* LGV Model */}
        {activeTab === "5seat" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-7xl mx-auto"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-white">
                  <img src="/img/LGV 无背景.webp"
                    alt="長安跨越 V7-EV LGV"
                    className="w-full h-full object-contain"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
                {/* Price Badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-2xl shadow-2xl">
                  <p className="text-sm font-medium opacity-90">{t("modelsShowcase.price")}</p>
                  <p className="text-3xl font-bold">Please contact us via WhatsApp</p>
                </div>
              </div>

              {/* Details */}
              <div>
                <h3 className="text-3xl md:text-4xl font-bold mb-2">長安跨越 V7-EV LGV</h3>
                <p className="text-blue-600 font-semibold text-lg mb-8">{t("modelsShowcase.available")}</p>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {specsLGV.map((spec, index) => {
                    const Icon = spec.icon;
                    return (
                      <motion.div
                        key={spec.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                        className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-500 mb-1">{spec.label}</p>
                            <p className="text-xl font-bold text-gray-900 truncate">{spec.value}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{spec.description}</p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Highlights */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-8">
                  <h4 className="text-white font-bold text-lg mb-4">{t("modelsShowcase.coreHighlights")}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {highlights.map((highlight) => (
                      <div key={highlight} className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="flex gap-4">
                  <Button className="flex-1 h-14 text-lg">
                    {t("common.bookTestDrive")}
                  </Button>
                  <Button variant="outline" className="flex-1 h-14 text-lg">
                    {t("common.learnMore")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

